import '../AdminDetailOverview.scss';
import './RecruitSeoReview.scss';
import './AggregatedManage.scss';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

// 크롤링 자동 게시 (server routers/recruit/autoPublish.js)
// 크롤러가 모은 공고(recruitDataPre)를 사이트 게시판에 올린다.
// 자동 모드를 켜면 크롤러가 저장하는 즉시 게시되고, 5분마다 밀린 공고도 처리한다.
// 상세내용 없음 · 중복 · 마감 지남 · 재수집 금지 · 오래된 공고는 올리지 않는다.
// 게시 가이드라인(board-import-field-check.md): 본문은 AI가 사실 정보만 뽑아 새로 쓰고, 공고가 아닌 글·수집거부 글은 거른다.

type Kind = 'minister' | 'overseas' | 'church' | 'institute';
type Tab = 'pending' | 'logs' | 'religiousbody';
type Filter = 'ready' | 'blocked' | 'all';

interface Settings {
  enabled: boolean;
  maxAgeDays: number;
  minBodyLength: number;
  updatedBy?: string | null;
  updatedAt?: string;
}

interface Status {
  settings: Settings;
  pending: number;
  today: { published: number; auto: number; failed: number };
}

interface PendingRow {
  id: number;
  source: string;
  title: string;
  church: string;
  /** 교회명 칸이 비어 제목·본문에서 찾은 경우 */
  churchInferred: 'title' | 'body' | null;
  /** 게시될 정식 교단명 · 크롤링 원래 값 */
  religiousbody: string;
  religiousbodyRaw: string;
  date: string;
  link: string;
  sort: string;
  location: string;
  bodyLength: number;
  deadline: string | null;
  suggestedKind: Kind;
  ready: boolean;
  reasons: string[];
  duplicateId: number | null;
  editingUser: string | null;
}

interface LogRow {
  id: number;
  preId: number;
  mode: 'AUTO' | 'MANUAL';
  result: 'PUBLISHED' | 'SKIPPED' | 'FAILED' | 'EXCLUDED' | 'DELETED';
  reason: string | null;
  kind: Kind | null;
  targetId: number | null;
  title: string | null;
  church: string | null;
  actor: string | null;
  createdAt: string;
}

interface AliasRow {
  id: number;
  keyword: string;
  /** '' = 교단 아님 (비움) */
  official: string;
  createdBy: string | null;
  createdAt: string;
}

const KIND_OPTIONS: [Kind, string][] = [
  ['minister', '국내사역지'],
  ['overseas', '해외사역지'],
  ['church', '찬양대/방송/직원'],
  ['institute', '학교/기관/단체'],
];
const KIND_LABEL = Object.fromEntries(KIND_OPTIONS) as Record<Kind, string>;
const RESULT_LABEL = { PUBLISHED: '게시', SKIPPED: '건너뜀', FAILED: '실패', EXCLUDED: '제외', DELETED: '삭제' };

export default function RecruitAutoPublish() {
  const [tab, setTab] = useState<Tab>('pending');
  const [status, setStatus] = useState<Status | null>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const loadStatus = useCallback(() => {
    axios.get(`${MainURL}/recruitautopublish/status`).then(res => setStatus(res.data)).catch(() => setStatus(null));
  }, []);
  useEffect(loadStatus, [loadStatus]);

  const refresh = () => {
    loadStatus();
    setReloadKey(k => k + 1);
  };

  const saveSettings = async (patch: Partial<Settings>) => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${MainURL}/recruitautopublish/settings`, patch);
      refresh();
    } catch (err: any) {
      alert(err.response?.data?.error || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => {
    if (!status) return;
    const next = !status.settings.enabled;
    const message = next
      ? `자동 게시를 켤까요?\n\n· 크롤러가 저장하는 공고가 바로 게시판에 올라갑니다.\n· 지금 대기 중인 공고 중 최근 ${status.settings.maxAgeDays}일 이내·점검 통과 공고도 곧바로 올라갑니다.\n· 올라간 공고는 '가져온 공고'로 30일 뒤 자동 비노출(교회가 인수하면 유지)됩니다.`
      : '자동 게시를 끌까요? 이후 크롤링된 공고는 대기 목록에 쌓입니다.';
    if (window.confirm(message)) void saveSettings({ enabled: next });
  };

  const editNumber = (key: 'maxAgeDays' | 'minBodyLength', label: string) => {
    if (!status) return;
    const value = window.prompt(label, String(status.settings[key]));
    if (value == null || value.trim() === '' || Number.isNaN(Number(value))) return;
    void saveSettings({ [key]: Number(value) });
  };

  const runNow = async () => {
    if (running || !window.confirm('대기 공고 중 점검을 통과한 공고를 지금 게시할까요? (한 번에 최대 50건)')) return;
    setRunning(true);
    try {
      await axios.post(`${MainURL}/recruitautopublish/run`);
      alert('자동 게시를 시작했습니다. 공고마다 본문을 새로 쓰느라 몇 분 걸릴 수 있습니다. 결과는 게시 기록 탭에서 확인해 주세요.');
      refresh();
    } catch (err: any) {
      alert(err.response?.data?.error || '실행하지 못했습니다.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="recruit-seo-review service-detail-overview agg-admin">
      <p className="service-detail-overview__hint">
        크롤러가 모은 공고를 사이트 게시판에 올립니다. <b>자동 게시</b>를 켜면 크롤링되는 즉시 게시되고, 5분마다 밀린 공고도 처리합니다.
        상세내용이 없거나 짧은 공고, 이미 올라간 공고(제목·교회·날짜 중복), 마감이 지난 공고, 재수집 금지 교회, 오래된 공고는 올리지 않습니다.
        올라간 공고는 '가져온 공고'로 잠금·30일 만료가 적용됩니다.
        <br />
        <b>게시 가이드라인</b> — 청빙 완료·공지·개인 사연·무단수집 거부 글·원문 링크 없는 글은 거르고, 끌어올림 글은 원글과 묶습니다.
        본문은 원문을 옮기지 않고 AI가 사실 정보(직분·자격·사례비·서류·기한 등)만 뽑아 새로 쓴 요약으로 올리며, 사진·첨부파일은 빼고 출처(원문 링크)를 표시합니다.
        이메일·전화는 본문에서 빼서 문의 칸에만 두고, 글쓴이·담당자 이름은 가립니다.
      </p>

      {status && (
        <div className="agg-admin__summary">
          <div>
            <span>자동 게시</span>
            <b className={status.settings.enabled ? 'agg-autopub__on' : ''}>{status.settings.enabled ? 'ON' : 'OFF'}</b>
            <button type="button" className="church-verify__toggle" disabled={saving} onClick={toggle} style={{ marginTop: 6 }}>
              {status.settings.enabled ? '끄기' : '켜기'}
            </button>
          </div>
          <div><span>대기 공고</span><b>{status.pending}</b><small>최근 {status.settings.maxAgeDays}일 이내</small></div>
          <div><span>오늘 게시</span><b>{status.today.published}</b><small>자동 {status.today.auto} · 실패 {status.today.failed}</small></div>
          <div>
            <span>게시 대상 기간</span>
            <b>{status.settings.maxAgeDays}일</b>
            <button type="button" className="church-verify__toggle" disabled={saving} onClick={() => editNumber('maxAgeDays', '공고 날짜가 며칠 이내인 공고만 게시할까요? (1~365)')} style={{ marginTop: 6 }}>변경</button>
          </div>
          <div>
            <span>최소 본문 길이</span>
            <b>{status.settings.minBodyLength}자</b>
            <button type="button" className="church-verify__toggle" disabled={saving} onClick={() => editNumber('minBodyLength', '상세내용이 몇 자 이상인 공고만 게시할까요? (태그 제외)')} style={{ marginTop: 6 }}>변경</button>
          </div>
          {status.settings.updatedBy && (
            <div><span>마지막 변경</span><small>{status.settings.updatedBy}<br />{status.settings.updatedAt?.slice(0, 16).replace('T', ' ')}</small></div>
          )}
        </div>
      )}

      <div className="service-detail-overview__tabs">
        {([['pending', '대기 공고'], ['logs', '게시 기록'], ['religiousbody', '교단 표기']] as [Tab, string][]).map(([key, label]) => (
          <button type="button" key={key} className={`service-detail-overview__tab${tab === key ? ' is-active' : ''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'pending' && <PendingTab key={reloadKey} onChanged={refresh} running={running} onRunNow={runNow} />}
      {tab === 'logs' && <LogsTab key={reloadKey} />}
      {tab === 'religiousbody' && <ReligiousBodyTab key={reloadKey} />}
    </div>
  );
}

function PendingTab({ onChanged, running, onRunNow }: { onChanged: () => void; running: boolean; onRunNow: () => void }) {
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PendingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [scanned, setScanned] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Record<number, Kind>>({});
  const [kinds, setKinds] = useState<Record<number, Kind>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${MainURL}/recruitautopublish/pending`, { params: { filter, page } });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setScanned(res.data.scanned || 0);
      setPageSize(res.data.pageSize || 20);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);
  useEffect(() => { void load(); }, [load]);

  const kindOf = (row: PendingRow) => kinds[row.id] || row.suggestedKind;
  const toggleRow = (row: PendingRow) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id];
      else next[row.id] = kindOf(row);
      return next;
    });
  };
  const selectAllPage = () => {
    const next: Record<number, Kind> = {};
    items.forEach(r => { next[r.id] = kindOf(r); });
    setSelected(next);
  };

  const allSelected = items.length > 0 && items.every(r => selected[r.id]);
  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    selectAllPage();
  };

  const removeSelected = async () => {
    const ids = Object.keys(selected).map(Number);
    if (busy || !ids.length) return;
    if (!window.confirm(`선택한 대기 공고 ${ids.length}건을 삭제할까요?\n삭제한 공고는 되돌릴 수 없습니다. (이미 게시된 공고는 지우지 않습니다)`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${MainURL}/recruitautopublish/delete`, { ids });
      alert(`${res.data.deleted ?? 0}건을 삭제했습니다.`);
      setSelected({});
      await load();
      onChanged();
    } catch (err: any) {
      alert(err.response?.data?.error || '삭제하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const editChurch = async () => {
    const rows = items.filter(r => selected[r.id]);
    if (busy || !rows.length) return;
    const message = rows.length === 1
      ? `#${rows[0].id} 공고의 교회명을 입력해 주세요.`
      : `선택한 ${rows.length}건의 교회명을 모두 아래 이름으로 바꿉니다.`;
    const value = window.prompt(message, rows.length === 1 ? rows[0].church || '' : '');
    if (value == null) return;
    if (!value.trim()) {
      alert('교회명을 입력해 주세요.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${MainURL}/recruitautopublish/church`, { ids: rows.map(r => r.id), church: value });
      if (rows.length > 1) alert(`${res.data.updated ?? 0}건의 교회명을 바꿨습니다.`);
      await load();
    } catch (err: any) {
      alert(err.response?.data?.error || '교회명을 바꾸지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const publish = async (rows: { id: number; kind: Kind }[], force = false) => {
    if (busy || !rows.length) return;
    const message = force
      ? `점검 사유를 무시하고 ${rows.length}건을 게시할까요? (이미 게시됨·중복은 그래도 막습니다)`
      : `${rows.length}건을 게시판에 올릴까요?`;
    if (!window.confirm(message)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${MainURL}/recruitautopublish/publish`, { items: rows, force });
      const failed = (res.data.results || []).filter((r: any) => !r.ok);
      alert(`게시 ${res.data.published}건${failed.length ? `\n못 올린 공고 ${failed.length}건: ${failed.map((r: any) => `#${r.id} ${r.reason}`).join(', ')}` : ''}`);
      setSelected({});
      await load();
      onChanged();
    } catch (err: any) {
      alert(err.response?.data?.error || '게시하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const selectedRows = Object.entries(selected).map(([id, kind]) => ({ id: Number(id), kind }));
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className="agg-admin__chips">
        {([['all', '전체'], ['ready', '게시 가능'], ['blocked', '보류(점검 사유 있음)']] as [Filter, string][]).map(([key, label]) => (
          <button type="button" key={key} className={filter === key ? 'is-active' : ''} onClick={() => { setFilter(key); setPage(1); setSelected({}); }}>
            {label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={selectAllPage} disabled={busy || !items.length}>이 페이지 전체 선택</button>
        <button type="button" className="is-active" disabled={busy || !selectedRows.length} onClick={() => publish(selectedRows)}>
          선택 {selectedRows.length}건 게시
        </button>
        <button type="button" disabled={busy || !selectedRows.length} onClick={() => publish(selectedRows, true)}>
          선택 {selectedRows.length}건 그래도 게시
        </button>
        <button type="button" disabled={busy || !selectedRows.length} onClick={editChurch}>
          선택 {selectedRows.length}건 교회명 변경
        </button>
        <button type="button" className="agg-autopub__delete" disabled={busy || !selectedRows.length} onClick={removeSelected}>
          선택 {selectedRows.length}건 삭제
        </button>
        <button type="button" disabled={running} onClick={onRunNow}>{running ? '실행 중...' : '자동 게시 지금 실행'}</button>
      </div>
      <p className="church-verify__sub">게시 대상 기간 안의 대기 공고 {scanned}건을 점검했습니다. 기간이 지난 공고는 목록에 표시하지 않습니다. 게시판은 제목·직무로 추천하며, 행마다 바꿀 수 있습니다.</p>

      {loading && <p className="church-verify__empty">점검 중...</p>}
      {!loading && items.length === 0 && <p className="church-verify__empty">공고가 없습니다.</p>}

      {!loading && items.length > 0 && (
        <div className="service-detail-overview__table-x">
          <table className="church-verify__table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} title="이 페이지 전체 선택" />
                </th>
                <th>공고</th>
                <th>출처 · 날짜</th>
                <th>본문 · 마감</th>
                <th>게시판</th>
                <th>점검</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map(row => (
                <tr key={row.id}>
                  <td>
                    <input type="checkbox" checked={Boolean(selected[row.id])} onChange={() => toggleRow(row)} />
                  </td>
                  <td>
                    {row.church || '(교회명 없음)'}
                    {row.churchInferred && (
                      <span className="church-verify__sub"> ({row.churchInferred === 'title' ? '제목' : '본문'}에서 찾음)</span>
                    )}
                    <div className="church-verify__sub">#{row.id} · {row.title || '(제목 없음)'}</div>
                    {(row.sort || row.location) && <div className="church-verify__sub">{[row.sort, row.location].filter(Boolean).join(' · ')}</div>}
                    {(row.religiousbody || row.religiousbodyRaw) && (
                      <div className="church-verify__sub">
                        교단: {row.religiousbody || '(없음)'}
                        {row.religiousbodyRaw && row.religiousbodyRaw !== row.religiousbody && ` ← 원래 '${row.religiousbodyRaw}'`}
                      </div>
                    )}
                    {row.editingUser && <div className="church-verify__sub">작업 중: {row.editingUser}</div>}
                  </td>
                  <td>
                    {row.link ? <a href={row.link} target="_blank" rel="noopener noreferrer">{row.source || '원문'}</a> : row.source}
                    <div className="church-verify__sub">{row.date}</div>
                  </td>
                  <td>
                    {row.bodyLength}자
                    <div className="church-verify__sub">{row.deadline ? `${row.deadline} 마감` : '채용 시 마감'}</div>
                  </td>
                  <td>
                    <select
                      value={kindOf(row)}
                      onChange={(e) => {
                        const kind = e.target.value as Kind;
                        setKinds(prev => ({ ...prev, [row.id]: kind }));
                        setSelected(prev => (prev[row.id] ? { ...prev, [row.id]: kind } : prev));
                      }}
                    >
                      {KIND_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </td>
                  <td>
                    {row.ready ? <span className="agg-autopub__ok">게시 가능</span> : row.reasons.map(r => <div key={r} className="agg-autopub__reason">{r}</div>)}
                    {row.duplicateId && (
                      <div className="church-verify__sub">
                        <a href={`/recruit/${row.suggestedKind}/${row.duplicateId}`} target="_blank" rel="noopener noreferrer">게시된 공고 #{row.duplicateId}</a>
                      </div>
                    )}
                  </td>
                  <td className="agg-admin__actions">
                    {row.ready ? (
                      <button type="button" className="church-verify__toggle" disabled={busy} onClick={() => publish([{ id: row.id, kind: kindOf(row) }])}>게시</button>
                    ) : !row.duplicateId ? (
                      <button type="button" className="church-verify__toggle" disabled={busy} onClick={() => publish([{ id: row.id, kind: kindOf(row) }], true)}>그래도 게시</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="church-verify__pager">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
        <span>{page} / {lastPage} · {total}건</span>
        <button type="button" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>다음</button>
      </div>
    </>
  );
}

function LogsTab() {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    axios.get(`${MainURL}/recruitautopublish/logs`, { params: { page } })
      .then(res => {
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
        setPageSize(res.data.pageSize || 20);
      })
      .catch(() => setItems([]));
  }, [page]);

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      {items.length === 0 && <p className="church-verify__empty">아직 기록이 없습니다.</p>}
      <table className="church-verify__table">
        <thead>
          <tr>
            <th>시각</th>
            <th>방식</th>
            <th>결과</th>
            <th>공고</th>
            <th>게시판</th>
            <th>사유 · 처리자</th>
          </tr>
        </thead>
        <tbody>
          {items.map(row => (
            <tr key={row.id}>
              <td>{String(row.createdAt).slice(0, 16).replace('T', ' ')}</td>
              <td>{row.mode === 'AUTO' ? '자동' : '수동'}</td>
              <td className={row.result === 'PUBLISHED' ? 'agg-autopub__ok' : 'agg-autopub__reason'}>{RESULT_LABEL[row.result]}</td>
              <td>
                {row.church}
                <div className="church-verify__sub">#{row.preId} · {row.title}</div>
              </td>
              <td>
                {row.kind && row.targetId ? (
                  <a href={`/recruit/${row.kind}/${row.targetId}`} target="_blank" rel="noopener noreferrer">{KIND_LABEL[row.kind]} #{row.targetId}</a>
                ) : row.kind ? KIND_LABEL[row.kind] : '-'}
              </td>
              <td>
                {row.reason || '-'}
                {row.actor && <div className="church-verify__sub">{row.actor}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="church-verify__pager">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
        <span>{page} / {lastPage} · 총 {total}건</span>
        <button type="button" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>다음</button>
      </div>
    </>
  );
}

const NOT_RELIGIOUS_BODY = '';
const officialLabel = (official: string) => official || '교단 아님(비움)';

// 교단 표기 — 정식 교단명마다 바꿀 단어('합동, 예장 합동, 예장합동')를 적어 두면 게시할 때 정식 교단명으로 바꾼다 (server routers/recruit/religiousBody.js)
const splitWords = (text: string) => text.split(/[,，\n]/).map(w => w.trim()).filter(Boolean);

function ReligiousBodyTab() {
  const [officials, setOfficials] = useState<string[]>([]);
  const [aliases, setAliases] = useState<AliasRow[]>([]);
  const [unmatched, setUnmatched] = useState<{ value: string; count: number }[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const wordsOf = useCallback((official: string, rows: AliasRow[]) => rows.filter(a => a.official === official).map(a => a.keyword).join(', '), []);

  const load = useCallback(() => {
    axios.get(`${MainURL}/recruitautopublish/religiousbody`)
      .then(res => {
        const rows: AliasRow[] = res.data.aliases || [];
        const list: string[] = res.data.officials || [];
        setOfficials(list);
        setAliases(rows);
        setUnmatched(res.data.unmatched || []);
        setDrafts(Object.fromEntries([...list, NOT_RELIGIOUS_BODY].map(o => [o, wordsOf(o, rows)])));
      })
      .catch(() => setAliases([]));
  }, [wordsOf]);
  useEffect(load, [load]);

  const report = (res: any) => {
    const conflicts: { word: string; official: string }[] = res.data.conflicts || [];
    if (conflicts.length) {
      alert(`다른 교단에 이미 있어서 넣지 않은 단어:\n${conflicts.map(c => `· ${c.word} → ${officialLabel(c.official)}`).join('\n')}`);
    }
  };

  const saveRow = async (official: string) => {
    if (busy) return;
    setBusy(true);
    try {
      report(await axios.post(`${MainURL}/recruitautopublish/religiousbody/words`, { official, words: splitWords(drafts[official] || '') }));
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const addUnmatched = async (value: string) => {
    if (busy) return;
    const official = picks[value] ?? NOT_RELIGIOUS_BODY;
    setBusy(true);
    try {
      report(await axios.post(`${MainURL}/recruitautopublish/religiousbody/word`, { official, word: value }));
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const groups = [...officials, NOT_RELIGIOUS_BODY];

  return (
    <>
      <p className="church-verify__sub">
        정식 교단명마다 바꿀 단어를 쉼표(,)로 나눠 적고 저장하면, 크롤링된 교단 값이 그 단어와 같을 때 정식 교단명으로 바꿔 게시합니다. 띄어쓰기·대소문자는 무시합니다.
        단어에 없는 값도 '예장 합동', '대한예수교장로회(고신)'처럼 분명한 표기는 교단 칸·제목·본문에서 찾아 바꾸고, 그래도 없으면 교단을 비웁니다.
        '교단 아님'에 적은 단어('명', '소속' 등)는 교단을 비웁니다.
      </p>

      <table className="church-verify__table">
        <thead>
          <tr><th>정식 교단명</th><th>바꿀 단어 (쉼표로 구분)</th><th /></tr>
        </thead>
        <tbody>
          {groups.map(official => {
            const changed = (drafts[official] ?? '') !== wordsOf(official, aliases);
            return (
              <tr key={official || '__none'}>
                <td style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{officialLabel(official)}</td>
                <td style={{ width: '100%' }}>
                  <textarea
                    className="agg-religious__words"
                    rows={2}
                    placeholder={official === '대한예수교장로회고신' ? '예: 고신, 예장 고신, 예장고신' : '예: 단어1, 단어2'}
                    value={drafts[official] ?? ''}
                    onChange={(e) => setDrafts(prev => ({ ...prev, [official]: e.target.value }))}
                  />
                </td>
                <td className="agg-admin__actions">
                  <button type="button" className="church-verify__toggle" disabled={busy || !changed} onClick={() => saveRow(official)}>저장</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h4 style={{ margin: '24px 0 8px' }}>단어에 없는 크롤링 값 {unmatched.length}개</h4>
      {unmatched.length === 0 ? (
        <p className="church-verify__empty">대기 공고의 교단 값은 모두 단어 목록에 있습니다.</p>
      ) : (
        <>
          <p className="church-verify__sub">아직 게시하지 않은 공고의 교단 값 중 정식 교단명·단어 목록에 없는 값입니다. 정식 교단명을 골라 추가하면 그 교단의 단어에 더해집니다.</p>
          <table className="church-verify__table">
            <thead>
              <tr><th>크롤링 값</th><th>대기 공고</th><th>정식 교단명</th><th /></tr>
            </thead>
            <tbody>
              {unmatched.map(u => (
                <tr key={u.value}>
                  <td>{u.value}</td>
                  <td>{u.count}건</td>
                  <td>
                    <select value={picks[u.value] ?? NOT_RELIGIOUS_BODY} disabled={busy} onChange={(e) => setPicks(prev => ({ ...prev, [u.value]: e.target.value }))}>
                      {groups.map(o => <option key={o || '__none'} value={o}>{officialLabel(o)}</option>)}
                    </select>
                  </td>
                  <td className="agg-admin__actions">
                    <button type="button" className="church-verify__toggle" disabled={busy} onClick={() => addUnmatched(u.value)}>추가</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
